import { BodyPrimary, H5 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemModelSellerId, getItemModelSellerName } from '@/redux/modules/item/summary/itemSummary.selectors';
import { HourglassLow, Info } from '@liveauctioneers/hammer-ui-core/icons';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import ItemBiddingStatusRow from '@/components/ItemBidding/ItemBiddingStatusRow';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styled from 'styled-components';
import Tooltip from '@liveauctioneers/caterwaul-components/lib/Tooltip/Tooltip';

export type TimedPlusItemBiddingPendingRegistrationProps = {
    itemId: number;
};

const TimedPlusItemBiddingPendingRegistration = ({ itemId }: TimedPlusItemBiddingPendingRegistrationProps) => {
    const dispatch = useAppDispatch();
    const sellerId = useAppSelector((state) => getItemModelSellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemModelSellerName(state, itemId));

    return (
        <StyledContent>
            <StyledHeader
                $darkGrey
                as="div"
            >
                <HourglassLow
                    color="primary"
                    size="sm"
                    style="fill"
                />
                <FormattedMessage
                    id="itemPage.biddingInfo.timedPlus.pendingRegistration.header"
                    values={{
                        info: (
                            <Tooltip
                                placement="bottom"
                                trigger={
                                    <Info
                                        className="ml-xs"
                                        color="primary"
                                        size="sm"
                                        style="fill"
                                    />
                                }
                            >
                                <FormattedMessage
                                    id="itemPage.biddingInfo.timedPlus.pendingRegistration.infoText"
                                    values={{
                                        messageTheAuctionHouse: (
                                            <Link
                                                key="timed-plus-pending-registration-message-the-auction-house-link"
                                                onClick={() => {
                                                    dispatch(
                                                        openSendSellerMessageModal(
                                                            {},
                                                            sellerId,
                                                            sellerName,
                                                            'Registration Approval Request'
                                                        )
                                                    );
                                                }}
                                            >
                                                <FormattedMessage id="itemPage.biddingInfo.timedPlus.pendingRegistration.messageTheAuctionHouse" />
                                            </Link>
                                        ),
                                    }}
                                />
                            </Tooltip>
                        ),
                    }}
                />
            </StyledHeader>
            <StyledDescription>
                <BodyPrimary $darkGrey>
                    <FormattedMessage id="itemPage.biddingInfo.timedPlus.pendingRegistration.description" />
                </BodyPrimary>
            </StyledDescription>
            <StyledSeparator />
            <ItemBiddingStatusRow itemId={itemId} />
        </StyledContent>
    );
};

export default TimedPlusItemBiddingPendingRegistration;

const StyledContent = styled.div`
    flex: 1 1 auto;
    padding: 32px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding: 24px;
    }
    @media print {
        padding: 0;
    }
    & > a {
        display: block;
    }
    & > *:last-child {
        margin-bottom: 0;
    }
    > div:not(:first-child),
    a {
        @media print {
            display: none;
        }
    }
    > div:first-child {
        @media print {
            margin: 0;
        }
    }
`;

const StyledHeader = styled(H5)`
    display: flex;
    align-items: center;
    text-transform: uppercase;
    margin-bottom: 0;
    > svg {
        margin-right: 8px;
    }
`;

const StyledDescription = styled.div`
    margin-top: 6px;
`;

const StyledSeparator = styled.hr`
    border: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    margin: 24px 0;
`;
